import { useEffect, useRef, useState } from 'react';

import { Axios } from '@utils/api/axios-instance.ts';

/**
 * Auto-detects the spoken language of an uploaded media file by extracting
 * a ~5s snippet client-side and POSTing it to /api/v3/speech-to-text/detect-language.
 *
 * Fail-open by design: any failure (decode error, network error, unsupported
 * codec, backend 204 / 5xx) resolves to `failed` silently — the caller should
 * keep whatever default the user already had selected.
 */

export type DetectionState =
  | { status: 'idle' }
  | { status: 'detecting' }
  | { status: 'detected'; language: string; confidence: number }
  | { status: 'failed' };

const SAMPLE_SECONDS = 5;
const TARGET_SR = 16000;
// Only read the head of the file rather than the whole thing. We just need a ~5s
// snippet; reading and decoding a multi-GB / multi-hour recording in full
// (file.arrayBuffer() + decodeAudioData over the entire file) exhausts the tab's
// memory and crashes the page ("out of memory"). 16 MB of leading bytes
// comfortably contains 5s at any realistic bitrate and bounds peak memory
// regardless of file size.
const MAX_DECODE_BYTES = 16 * 1024 * 1024;

async function extractSnippetWav(file: File): Promise<Blob | null> {
  // Only attempt for files the browser can decode (audio + some video).
  const AudioCtor: typeof AudioContext | undefined =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (window as any).AudioContext || (window as any).webkitAudioContext;
  if (!AudioCtor) return null;

  let ctx: AudioContext | null = null;
  try {
    const head =
      file.size > MAX_DECODE_BYTES ? file.slice(0, MAX_DECODE_BYTES) : file;
    const buf = await head.arrayBuffer();
    ctx = new AudioCtor({ sampleRate: TARGET_SR });
    // decodeAudioData detaches `buf`; we never reuse it, so no defensive copy.
    const decoded = await ctx.decodeAudioData(buf);

    // We only use the first SAMPLE_SECONDS, so bound every per-sample loop to
    // that window — a long decoded buffer must not allocate full-length PCM.
    const srcRate = decoded.sampleRate;
    const neededSrc = Math.min(
      decoded.length,
      Math.ceil(SAMPLE_SECONDS * srcRate) + 2
    );

    // Downmix to mono (needed window only).
    const mono = new Float32Array(neededSrc);
    for (let ch = 0; ch < decoded.numberOfChannels; ch++) {
      const data = decoded.getChannelData(ch);
      for (let i = 0; i < neededSrc; i++) mono[i] += data[i];
    }
    if (decoded.numberOfChannels > 1) {
      for (let i = 0; i < neededSrc; i++) mono[i] /= decoded.numberOfChannels;
    }

    // Resample to 16 kHz with linear interpolation.
    const targetLength = Math.min(
      Math.floor(SAMPLE_SECONDS * TARGET_SR),
      Math.floor((neededSrc / srcRate) * TARGET_SR)
    );
    const out = new Float32Array(targetLength);
    const ratio = srcRate / TARGET_SR;
    for (let i = 0; i < targetLength; i++) {
      const srcIdx = i * ratio;
      const lo = Math.floor(srcIdx);
      const hi = Math.min(lo + 1, neededSrc - 1);
      const frac = srcIdx - lo;
      out[i] = mono[lo] * (1 - frac) + mono[hi] * frac;
    }

    return encodeWav(out, TARGET_SR);
  } catch {
    return null;
  } finally {
    if (ctx) {
      try {
        await ctx.close();
      } catch {
        /* ignore */
      }
    }
  }
}

function encodeWav(samples: Float32Array, sampleRate: number): Blob {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);
  const writeStr = (offset: number, s: string) => {
    for (let i = 0; i < s.length; i++)
      view.setUint8(offset + i, s.charCodeAt(i));
  };
  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);
  view.setUint16(34, 16, true);
  writeStr(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  let offset = 44;
  for (let i = 0; i < samples.length; i++) {
    const clamped = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(
      offset,
      clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff,
      true
    );
    offset += 2;
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

export function useLanguageDetection(file: File | null): DetectionState {
  const [state, setState] = useState<DetectionState>({ status: 'idle' });
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!file) {
      setState({ status: 'idle' });
      return;
    }

    const controller = new AbortController();
    abortRef.current?.abort();
    abortRef.current = controller;

    let cancelled = false;
    setState({ status: 'detecting' });

    (async () => {
      const wav = await extractSnippetWav(file);
      if (cancelled || controller.signal.aborted) return;
      if (!wav) {
        setState({ status: 'failed' });
        return;
      }

      const form = new FormData();
      form.append('file', wav, 'snippet.wav');

      // Single quick retry on 5xx / network error. The single-replica
      // staging api2 occasionally drops the connection mid-proxy under
      // concurrent detect-language calls → nginx 502. A small backoff
      // retry masks that transient failure without escalating to UI.
      const attempt = async (): Promise<{
        status: number;
        data: { language?: string; confidence?: number } | undefined;
      } | null> => {
        try {
          const { status, data } = await Axios.post(
            '/api/v3/speech-to-text/detect-language',
            form,
            { signal: controller.signal, validateStatus: () => true }
          );
          return { status, data };
        } catch {
          return null;
        }
      };

      let res = await attempt();
      const transient =
        !res || (res.status >= 500 && res.status < 600) || res.status === 0;
      if (transient && !cancelled && !controller.signal.aborted) {
        await new Promise(r => setTimeout(r, 400));
        if (!cancelled && !controller.signal.aborted) {
          res = await attempt();
        }
      }

      if (cancelled || controller.signal.aborted) return;

      // 204 No Content = backend says "skip silently", treat as failed.
      // Anything else non-200, or malformed body: also failed.
      if (
        !res ||
        res.status !== 200 ||
        !res.data ||
        typeof res.data.language !== 'string'
      ) {
        setState({ status: 'failed' });
        return;
      }
      setState({
        status: 'detected',
        language: res.data.language,
        confidence:
          typeof res.data.confidence === 'number' ? res.data.confidence : 0
      });
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [file]);

  return state;
}
